import React, { useState } from 'react';
import IconContainer from './IconContainer';
import PropTypes from 'prop-types';
import clsx from 'clsx';

const cloneIconWithTabIndex = (icon) => {
  return icon ? React.cloneElement(icon, { tabIndex: -1 }) : null;
};

const convertColorToRgba = (color, opacity = 1) => {
  if (color.startsWith('#')) {
    let hex = color.replace('#', '');
    if (hex.length === 3) {
      hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${opacity})`;
  }

  const colorMapping = {
    'var(--red-color)': 'rgba(var(--red-color-rgb), opacity)',
    'var(--dark-color)': 'rgba(var(--dark-color-rgb), opacity)',
    'var(--white-color)': 'rgba(var(--white-color-rgb), opacity)',
    '#148be6': 'rgba(20, 139, 230, opacity)',
    '#a649f1': 'rgba(166, 73, 241, opacity)'
  };

  const mapped = colorMapping[color];
  if (mapped) {
    return mapped.replace('opacity', opacity);
  }

  if (color.startsWith('rgb(')) {
    const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      return `rgba(${rgbMatch[1]},${rgbMatch[2]},${rgbMatch[3]},${opacity})`;
    }
  }

  return color;
};

const AppButton = ({ children, ...props }) => {
  const {
    onClick,
    label,
    minimize,
    id,
    tabIndex,
    onFocus,
    onKeyDown,
    color,
    minContent,
    lang,
    selectedType,
    isActionElement = false,
    style,
    classNameChildren,
    className,
    dataCy,
    opacity = 1
  } = props;

  const actionPadding = isActionElement ? '0px 5px 0px 5px' : '0px 10px 0px 10px';
  const [hover, setHover] = useState(false);

  let variant;
  if (typeof props.variant === 'undefined')
    variant = 'basic';
  else
    variant = props.variant;

  const classes = {
    button: {
      cursor: 'pointer',
      height: (props.id === 'languages' ? 'fit-content' : 'auto'),
    },
    disabled: {
      height: (props.id === 'languages' ? 'fit-content' : 'auto'),
    },
    light: {
      paddingLeft: (props.paddingWidth ? props.paddingWidth + 'px' : 0),
      paddingRight: (props.paddingWidth ? props.paddingWidth + 'px' : 0),
      paddingTop: (props.padding ? props.padding + 'px' : 0),
      paddingBottom: (props.padding ? props.padding + 'px' : 0),
      width: (props.width ? props.width : ''),
      color: (props) => {
        switch (props.color) {
        case 'secondary':
          return 'var(--red-color)';
        case 'info':
          return '#148be6';
        case 'violet':
          return '#a649f1';
        default:
          return 'var(--dark-color)';
        }
      },
      border: 'solid 1px',
      backgroundColor: convertColorToRgba('var(--white-color)', opacity),
      '&.selectedType': {
        backgroundColor: props.disabled ? '' : props.color === 'secondary'
          ? convertColorToRgba('var(--red-color)', opacity) : props.color === 'info'
            ? convertColorToRgba('#148be6', opacity) : props.color === 'violet'
              ? convertColorToRgba('#a649f1', opacity) : convertColorToRgba('var(--dark-color)', opacity),
        color: (props.disabled ? 'var(--red-color)' : 'var(--white-color)'),
      },
    },
    outlined: {
      paddingLeft: (props.paddingWidth ? props.paddingWidth + 'px' : 0),
      paddingRight: (props.paddingWidth ? props.paddingWidth + 'px' : 0),
      paddingTop: (props.padding ? props.padding + 'px' : 0),
      paddingBottom: (props.padding ? props.padding + 'px' : 0),
      border: 'dashed 4px',
      color: (props.color === 'secondary' ? 'var(--red-color)' : 'var(--dark-color)'),
    },
    contained: {
      paddingLeft: (props.paddingWidth ? props.paddingWidth + 'px' : 0),
      paddingRight: (props.paddingWidth ? props.paddingWidth + 'px' : 0),
      paddingTop: (props.padding ? props.padding + 'px' : 0),
      paddingBottom: (props.padding ? props.padding + 'px' : 0),
      width: (props.width ? props.width : 'unset'),
      color: (props.color === 'secondary' ? 'var(--white-color)' : 'var(--white-color)'),
      border: (props.noBorder ? 'solid 0px' : 'solid 1px'),
      backgroundColor: props.color === 'secondary' ? convertColorToRgba('var(--red-color)', opacity) : convertColorToRgba('var(--dark-color)', opacity),
    },
    basic: {
      paddingLeft: (props.paddingWidth ? props.paddingWidth + 'px' : 0),
      paddingRight: (props.paddingWidth ? props.paddingWidth + 'px' : 0),
      paddingTop: (props.padding ? props.padding + 'px' : 0),
      paddingBottom: (props.padding ? props.padding + 'px' : 0),
      color: (props.color === 'secondary' ? 'var(--red-color)' : 'var(--dark-color)'),
    },
    minContent: {
      width: '80%',
    },
    buttonApp: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: actionPadding,
    },
    impact: {
      backgroundColor: (props.disabled ? '' : props.color === 'secondary'
        ? convertColorToRgba('var(--red-color)', opacity) : props.color === 'info'
          ? convertColorToRgba('#148be6', opacity) : props.color === 'violet'
            ? convertColorToRgba('#a649f1', opacity) : convertColorToRgba('var(--dark-color)', opacity)),
      color: (props.disabled ? 'var(--red-color)' : 'var(--white-color)'),
      textTransform: 'uppercase',
      paddingLeft: (props.paddingWidth ? props.paddingWidth + 'px' : 0),
      paddingRight: (props.paddingWidth ? props.paddingWidth + 'px' : 0),
      paddingTop: (props.padding ? props.padding + 'px' : 0),
      paddingBottom: (props.padding ? props.padding + 'px' : 0),
      width: (props.width ? props.width : 'unset'),
      fontWeight: '900',
    }
  };
  const classesHover = {
    light: {
      backgroundColor: props.disabled ? '' : props.color === 'secondary'
        ? convertColorToRgba('var(--red-color)', opacity) : props.color === 'info'
          ? convertColorToRgba('#148be6', opacity) : props.color === 'violet'
            ? convertColorToRgba('#a649f1', opacity) : convertColorToRgba('var(--dark-color)', opacity),
      color: (props.disabled ? 'var(--red-color)' : 'var(--white-color)'),
    },
    outlined: {
      color: (props.color === 'secondary' ? 'var(--dark-color)' : 'var(--red-color)'),
    },
    contained: {
      border: (props.noBorder ? 'solid 0px' : '1px solid var(--red-color)'),
      color: (props.color === 'secondary' ? 'var(--red-color)' : 'var(--dark-color)'),
      backgroundColor: convertColorToRgba('var(--white-color)', opacity),
    },
    basic: {
      color: (props.color === 'secondary' ? 'var(--dark-color)' : 'var(--red-color)'),
    },
    impact : {
      color: (props.color === 'secondary' ? 'var(--red-color)' : 'var(--dark-color)'),
      backgroundColor: convertColorToRgba('var(--white-color)', opacity),
    }
  };

  let displayLang = '';
  if (lang) {
    displayLang = lang.toLowerCase();
  }
  const [leftIcon, setLeftIcon] = React.useState(cloneIconWithTabIndex(props.leftIcon));
  const [rightIcon, setRightIcon] = React.useState(cloneIconWithTabIndex(props.rightIcon));
  return (
    <button
      aria-label={label}
      className={clsx({['selectedType']: Boolean(selectedType)}, className)}
      data-cy={dataCy}
      disabled={props.disabled}
      id={id}
      lang={displayLang}
      onClick={onClick}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onMouseEnter={() => {
        setHover(true);
        if (props.leftIcon) {
          setLeftIcon(
            <IconContainer
              color={variant === 'light' ? 'WHITE' : color === 'primary' ? 'BLACK' : 'RED'}
              icon={props.leftIcon?.props?.icon === 'ACCOUNT_RED' ? 'ACCOUNT_WHITE' : props.leftIcon?.props?.icon}
              noHover
              size={props.leftIcon?.props?.size}
              svgClassname={props.leftIcon?.props?.svgClassname}
              tabIndex={-1}
            >
            </IconContainer>
          );
        }
        if (props.rightIcon) {
          setRightIcon(
            <IconContainer
              color={variant === 'light' ? 'WHITE' : color === 'primary' ? 'BLACK' : 'RED'}
              icon={props.rightIcon?.props?.icon}
              noHover
              size={props.rightIcon?.props?.size}
              svgClassname={props.rightIcon?.props?.svgClassname}
              tabIndex={-1}
            >
            </IconContainer>);
        }
      }}
      onMouseLeave={() => {
        setHover(false);
        if (leftIcon) setLeftIcon(props.leftIcon);
        if (rightIcon) setRightIcon(props.rightIcon);
      }}
      style={{
        ...classes.button,
        ...classes[variant],
        ...(hover ? classesHover[variant]: {}),
        ...style,
      }}
      tabIndex={tabIndex}
      title={label}
    >
      <div
        style={{...classes.buttonApp}}
      >
        {leftIcon &&
        <div style={{ marginRight: 10 }}>
          {leftIcon}
        </div>}
        <div
          className={classNameChildren}
          style={{
            ...(minContent ? classes.minContent : {}),
            ...(minimize ? { textTransform: 'capitalize'} : { textTransform: 'uppercase'})
          }}
        >
          {children}
        </div>
        {rightIcon && (
          <div style={{ marginLeft: 10, border: 'none' }}>
            {rightIcon}
          </div>)}
      </div>
    </button>
  );
};

AppButton.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  classNameChildren: PropTypes.string,
  color: PropTypes.oneOf(['primary', 'secondary', 'info', 'violet']),
  dataCy: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  isActionElement: PropTypes.bool,
  label: PropTypes.string,
  lang: PropTypes.string,
  leftIcon: PropTypes.element,
  minContent: PropTypes.bool,
  minimize: PropTypes.bool,
  noBorder: PropTypes.bool,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyPress: PropTypes.func,
  opacity: PropTypes.number,
  padding: PropTypes.number,
  paddingWidth: PropTypes.number,
  rightIcon: PropTypes.element,
  selectedType: PropTypes.bool,
  style: PropTypes.object,
  tabIndex: PropTypes.any,
  variant: PropTypes.oneOf(['basic', 'light', 'outlined', 'contained', 'impact']),
  width: PropTypes.string
};

export default AppButton;
